import Vue from 'vue'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import utils from '@/mixins/utils.js'
import store from '@/store/store'
import { vue } from '@/main.js'

/**
 * Retorna os cadastros de uma equipe
 *
 * @param {int} cod_ibge - IBGE do município
 * @param {int} nu_ine - INE da Equipe
 *
 * @returns {Array}
 */
export async function getCadastrosPorEquipe(cod_ibge, nu_ine) {
  try {
    const { data } = await Vue.prototype.$api.get(
      `/app/media-atendimentos-por-pessoa-emulti/${cod_ibge}/equipe/${nu_ine}`
    )
    return data
  } catch (err) {
    console.log(err)
  }
}

/**
 * Retorna os cadastros por microárea
 *
 * @param {*} cod_ibge
 * @param {*} nu_ine
 * @param {*} micro_area
 *
 * @returns {Array}
 */
export async function getCadastrosPorMicroArea(cod_ibge, nu_ine, micro_area) {
  try {
    const { data } = await Vue.prototype.$api.get(
      `/app/media-atendimentos-por-pessoa-emulti/${cod_ibge}/equipe/${nu_ine}/microarea/${micro_area}`
    )
    return data
  } catch (err) {
    console.log(err)
  }
}

/**
 * Retorna o nome da cor de acordo com o valor
 * @param {Number} valor - Valor numérico
 * @returns {String}
 */
export function getCorTextoResultado(valor) {
  if (valor >= 60) {
    return 'green-color'
  } else {
    return 'red-color'
  }
}

/**
 * Lista de atendimentos a partir do JSON em `cadastro.dados` (mesmo formato da tabela expandida).
 *
 * @param {*} dados
 * @returns {Array}
 */
function parseListaAtendimentosPdf(dados) {
  if (dados == null || dados === '') return []
  try {
    const arr = typeof dados === 'string' ? JSON.parse(dados) : dados
    return Array.isArray(arr) ? arr : []
  } catch {
    return []
  }
}

/**
 * Gera o relatório em PDF.
 *
 * @param {Array} cadastros
 * @param {Object} selectEquipe
 * @param {String} microArea
 */
export function generatePdf(cadastros, selectEquipe, microArea) {
  let title_pdf = 'Pessoas atendidas'
  let titlePdfSave = utils.methods.titlePdf('Pessoas atendidas')

  let description_pdf = 'Pessoas atendidas por profisssionais da eMulti'
  const headers_pdf = [
    'NOME',
    'NASCIMENTO',
    'MÃE',
    'CPF',
    'CNS',
    'MICROÁREA',
    'CADASTRADO POR',
    'ATENDIMENTOS',
  ]
  const idxAtendimentos = 7

  function linhaPrincipal(c) {
    return [
      c.no_cidadao ?? '',
      (utils.methods.formatDate(c.dt_nascimento) || '') +
        `\n(${c.idade} ${c.idade > 1 ? 'anos' : 'ano'})`,
      c.no_mae ?? '',
      c.nu_cpf ?? '',
      c.nu_cns ?? '',
      c.micro_area ?? '',
      c.no_profissional ?? '',
      c.num_med_atend_pessoa,
    ]
  }

  const estiloHead = {
    fillColor: '#ccc',
    fontSize: 6,
    textColor: '#000',
    halign: 'center',
    cellWidth: 'wrap',
  }
  const estiloBody = {
    halign: 'center',
    fontSize: 6,
  }

  const estiloBodyPrincipal = {
    ...estiloBody,
    fillColor: [245, 245, 245],
  }

  const optsTabelaPrincipal = {
    theme: 'plain',
    styles: { lineWidth: 0 },
    tableLineWidth: 0,
  }

  const corBordaExpandida = [228, 228, 228]
  const optsTabelaExpandida = {
    theme: 'grid',
    styles: {
      lineWidth: 0.05,
      lineColor: corBordaExpandida,
    },
    tableLineWidth: 0,
  }

  let doc = new jsPDF({
    orientation: 'l',
    unit: 'pt',
    format: 'a4',
    ...utils.methods.encryptionPdf(),
  })

  utils.methods
    .fileToBase64(store.state.infoMunicipio.brasao)
    .then((base64data) => {
      // Header
      let verticalPosition = utils.methods.headerPdf(
        doc,
        base64data,
        title_pdf,
        description_pdf,
        200,
        'l'
      )

      verticalPosition = utils.methods.bodyPdf(
        doc,
        [
          {
            label: 'Equipe',
            value: selectEquipe.no_equipe,
          },
          {
            label: 'CNES',
            value: selectEquipe.nu_cnes,
          },
          {
            label: 'INE',
            value: selectEquipe.nu_ine,
          },
          {
            label: 'Microárea',
            value: microArea,
          },
        ],
        verticalPosition,
        'l'
      )

      let currentY = verticalPosition

      if (!cadastros.length) {
        doc.autoTable({
          head: [headers_pdf],
          body: [['—', '—', '—', '—', '—', '—', '—', '—']],
          headStyles: estiloHead,
          bodyStyles: estiloBodyPrincipal,
          ...optsTabelaPrincipal,
          startY: currentY,
          showHead: 'firstPage',
        })
      } else {
        for (let i = 0; i < cadastros.length; i++) {
          const c = cadastros[i]
          const optsPrincipal = {
            body: [linhaPrincipal(c)],
            headStyles: estiloHead,
            bodyStyles: estiloBodyPrincipal,
            ...optsTabelaPrincipal,
            startY: currentY,
            showHead: i === 0 ? 'firstPage' : 'never',
            willDrawCell: function (data) {
              if (data.row.section === 'head') {
                doc.setTextColor(0, 0, 0)
                return
              }
              const num_med_atend_pessoa = data.row.raw[idxAtendimentos]
              if (num_med_atend_pessoa == 1) {
                doc.setTextColor(255, 165, 0)
              } else {
                doc.setTextColor(2, 99, 57)
              }
            },
          }
          if (i === 0) optsPrincipal.head = [headers_pdf]
          doc.autoTable(optsPrincipal)
          currentY = doc.autoTable.previous.finalY

          const lista = parseListaAtendimentosPdf(c.dados)
          const corpoDetalhe = lista.map((a) => [
            a.dt_atendimento != null
              ? utils.methods.formatDate(a.dt_atendimento)
              : '',
            a.tp_atendimento ?? '',
            a.nu_ine ?? '',
          ])

          doc.autoTable({
            head: [['Data', 'Tipo', 'Equipe']],
            body:
              corpoDetalhe.length > 0
                ? corpoDetalhe
                : [['', 'Sem registros de atendimento', '']],
            headStyles: {
              ...estiloHead,
              fillColor: '#e8e8e8',
              halign: 'center',
              valign: 'middle',
              lineWidth: 0,
            },
            bodyStyles: {
              ...estiloBody,
              halign: 'center',
              valign: 'middle',
              lineWidth: 0.05,
              lineColor: corBordaExpandida,
            },
            columnStyles: {
              0: { cellWidth: 100, halign: 'center' },
              1: { cellWidth: 'auto', halign: 'center' },
              2: { cellWidth: 100, halign: 'center' },
            },
            ...optsTabelaExpandida,
            startY: currentY,
            willDrawCell: function (data) {
              doc.setTextColor(0, 0, 0)
            },
          })
          currentY = doc.autoTable.previous.finalY + 14
        }
      }

      utils.methods.footerPdf(doc, doc.autoTable.previous.finalY, 250, 600, 'l')

      doc.save(titlePdfSave + '.pdf')

      vue.$toast.success('PDF GERADO COM SUCESSO!', 'Sucesso:', {
        position: 'topRight',
      })
    })
    .catch((error) => {
      console.error(error)
      vue.$toast.error('ERRO AO GERAR PDF!', 'Erro:', {
        position: 'topRight',
      })
    })
}

/**
 * Percentual do município
 *
 * @param {Array} dadosDaTabelaResultado
 * @returns {Object}
 */
export function percentualMunicipio(dadosDaTabelaResultado) {
  let numerador = 0
  let denominador = 0
  let resultado = 0
  let classificacao = ''

  const hoje = new Date()
  const mesAtual = hoje.getMonth() + 1
  const anoAtual = String(hoje.getFullYear())
  const mesAnoAtual = `${mesAtual}/${anoAtual}`

  const dadosDoMunicipio = dadosDaTabelaResultado.find(
    (item) =>
      item.nivel == 1 &&
      item.ibge === store.state.cod_ibge &&
      item.indicador == 44 &&
      item.mes_ano === mesAnoAtual
  )

  if (dadosDoMunicipio === undefined) {
    return {
      numerador: 0,
      denominador: 0,
      resultado: 0,
      classificacao: '',
    }
  }

  let dados = JSON.parse(dadosDoMunicipio.dados)

  numerador = dados.numerador
  denominador = dados.denominador
  resultado = dados.resultado
  classificacao = dados.classificacao

  return {
    numerador: numerador,
    denominador: denominador,
    resultado: resultado,
    classificacao: classificacao,
  }
}

/**
 * Percentual da equipe
 *
 * @param {Array} dadosDaTabelaResultado
 * @param {String} nuIne
 * @returns {Object}
 */
export function percentualEquipe(dadosDaTabelaResultado, nuIne) {
  let numerador = 0
  let denominador = 0
  let resultado = 0
  let classificacao = ''

  const hoje = new Date()
  const mesAtual = hoje.getMonth() + 1
  const anoAtual = String(hoje.getFullYear())
  const mesAnoAtual = `${mesAtual}/${anoAtual}`

  let dadosDaEquipe = dadosDaTabelaResultado.find(
    (item) =>
      item.nivel == 2 &&
      item.ine === nuIne &&
      item.indicador == 44 &&
      item.mes_ano === mesAnoAtual
  )

  if (dadosDaEquipe === undefined) {
    return {
      numerador: 0,
      denominador: 0,
      resultado: 0,
      classificacao: '',
    }
  }

  let dados = JSON.parse(dadosDaEquipe.dados)

  numerador = dados.numerador
  denominador = dados.denominador
  resultado = dados.resultado
  classificacao = dados.classificacao

  return {
    numerador: numerador,
    denominador: denominador,
    resultado: resultado,
    classificacao: classificacao,
  }
}

/**
 * Percentual da microárea
 *
 * @param {Array} dadosDaTabelaResultado
 * @param {String} nu_ine
 * @param {String} micro_area
 * @returns {Object}
 */
export function percentualMicroArea(
  dadosDaTabelaResultado,
  nu_ine,
  micro_area
) {
  let numerador = 0
  let denominador = 0
  let resultado = 0
  let classificacao = ''

  const hoje = new Date()
  const mesAtual = hoje.getMonth() + 1
  const anoAtual = String(hoje.getFullYear())
  const mesAnoAtual = `${mesAtual}/${anoAtual}`

  let dadosDaMicroArea = dadosDaTabelaResultado.find(
    (item) =>
      item.nivel == 3 &&
      item.ine == nu_ine &&
      item.micro_area === micro_area &&
      item.indicador == 44 &&
      item.mes_ano === mesAnoAtual
  )

  if (dadosDaMicroArea === undefined) {
    return {
      numerador: 0,
      denominador: 0,
      resultado: 0,
      classificacao: classificacao,
    }
  }

  let dados = JSON.parse(dadosDaMicroArea.dados)

  numerador = dados.numerador
  denominador = dados.denominador
  resultado = dados.resultado
  classificacao = dados.classificacao

  return {
    numerador: numerador,
    denominador: denominador,
    resultado: resultado,
    classificacao: classificacao,
  }
}
